import React, { useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, CreditCard, Loader2, ImageIcon, X } from "lucide-react";

export default function PaymentReceiptDialog({ open, onOpenChange, bookingId, onSuccess }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Vui lòng chọn file ảnh");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File quá lớn (tối đa 10MB)");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleRemove = () => {
    setFile(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Vui lòng chọn ảnh biên nhận");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/customer/bookings/${bookingId}/confirm-payment`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Đã gửi biên nhận. Quản lý sẽ kiểm tra.");
      handleRemove();
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi gửi biên nhận");
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      handleRemove();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md" data-testid="receipt-upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-sky-400" />
            Xác nhận chuyển khoản
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs leading-relaxed">
            Quý khách vui lòng chụp lại ảnh màn hình giao dịch thành công để Karinox Arena đối soát.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Upload area */}
          {!preview ? (
            <label
              className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-[#334155] hover:border-sky-500/50 rounded-xl p-8 cursor-pointer transition-colors bg-[#0f172a]/50 hover:bg-sky-500/5"
              data-testid="receipt-upload-area"
            >
              <div className="w-12 h-12 rounded-full bg-sky-500/10 flex items-center justify-center">
                <Upload className="w-6 h-6 text-sky-400" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-slate-300">Nhấn để chọn ảnh biên nhận</p>
                <p className="text-[10px] text-slate-500 mt-1">PNG, JPG, JPEG (tối đa 10MB)</p>
              </div>
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                data-testid="receipt-file-input"
              />
            </label>
          ) : (
            <div className="relative" data-testid="receipt-preview">
              <img
                src={preview}
                alt="Biên nhận"
                className="w-full max-h-64 object-contain rounded-lg border border-[#334155]"
              />
              <button
                onClick={handleRemove}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500/80 hover:bg-red-500 text-white flex items-center justify-center transition-colors"
                data-testid="receipt-remove-btn"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                <ImageIcon className="w-3.5 h-3.5" />
                <span className="truncate">{file?.name}</span>
                <span className="text-slate-600">({(file?.size / 1024).toFixed(0)} KB)</span>
              </div>
            </div>
          )}

          <div className="bg-sky-500/5 border border-sky-500/20 rounded-lg p-3">
            <p className="text-[11px] text-sky-300 leading-relaxed">
              AI sẽ tự động phân tích số tiền và thời gian giao dịch từ biên nhận để đối soát với đơn đặt sân.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={uploading}
            className="text-slate-400">
            Huỷ
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={uploading || !file}
            className="bg-sky-500 hover:bg-sky-600 text-white font-bold gap-1.5"
            data-testid="receipt-submit-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Đang gửi..." : "Gửi biên nhận"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
